import React, { useLayoutEffect, useRef, useState,useEffect } from 'react'
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
// import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import dark_theme from "@amcharts/amcharts5/themes/Dark";
import { Container, Box, Typography, useTheme, IconButton, Tooltip,Select,FormControl,Menu,MenuItem ,Slider,InputLabel,Button} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { WATERMARK_CONFIG,DateFormat,USER_ROLE_FIELDS,MenuProps,BAR_CHART_ADMIN_PLOT_TYPE, BAR_CHART_OI_PLOT_TYPE, BAR_CHART_MI_PLOT_TYPE,MIN_BAR_CHART_WIDTH } from '../../../common/utility/constant';
import dayjs from 'dayjs';
import { DatePicker} from 'antd';
import useAuth from '../../../common/hooks/useAuth';
// ============================================================
// NEW - Centralized Permission System
// See: src/common/hooks/usePermissions.js
// ============================================================
import usePermissions from '../../../common/hooks/usePermissions';


// ------------------------------------------------------------
// Helpers for the categorical strike axis (pure functions)
// ------------------------------------------------------------

// smallest gap between two consecutive strikes
const getStrikeUnit = (data) => {
  const nums = (data || [])
    .map((item) => Number(item.strike))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  let unit = Infinity;
  for (let i = 1; i < nums.length; i++) {
    const diff = nums[i] - nums[i - 1];
    if (diff > 0 && diff < unit) unit = diff;
  }
  return Number.isFinite(unit) ? unit : 50;
};

// nearest strike (as string) to a numeric value
const getNearestStrike = (value, data) => {
  if (value == null || !data || data.length === 0) {
    return value == null ? undefined : String(value);
  }
  const nearest = data.reduce((closest, item) =>
    Math.abs(Number(item.strike) - value) < Math.abs(Number(closest.strike) - value) ? item : closest
  );
  return String(nearest.strike);
};

// 0..1 location of `value` inside the cell of its nearest strike
// (0.5 = centre of the strike, so the line sits at the exact value, not snapped)
const getCategoryLocation = (value, data) => {
  if (value == null || !data || data.length === 0) return 0.5;
  const nearest = Number(getNearestStrike(value, data));
  if (!Number.isFinite(nearest)) return 0.5;
  const unit = getStrikeUnit(data);
  const loc = 0.5 + (Number(value) - nearest) / unit;
  return Math.min(1, Math.max(0, loc));
};

// Spot low/high band on categorical strike axis: exact API prices within strike cells
const setSpotBandRange = (axisRange, spotLow, spotHigh, data) => {
  if (!axisRange || spotLow == null || spotHigh == null) return;
  // CategoryAxis ranges use categoryLocation / endCategoryLocation (not location / endLocation).
  // endCategoryLocation defaults to 1, which fills the whole end strike column — wrong for e.g. 22777.55.
  axisRange.setAll({
    category: getNearestStrike(spotLow, data) ?? "",
    endCategory: getNearestStrike(spotHigh, data) ?? "",
    categoryLocation: getCategoryLocation(spotLow, data),
    endCategoryLocation: getCategoryLocation(spotHigh, data),
  });
};

// vertical gap between rows of guide labels (Spot / Sigma / Tail)
const GUIDE_ROW_GAP = 28;


const OptChainChartContainer = (props) => {
  const seriesGexRef = useRef(null);
  const seriesGexMaxRef = useRef(null);
  const seriesGexMinRef = useRef(null);
  const seriesGexCeRef = useRef(null);
  const seriesCeMaxRef = useRef(null);
  const seriesCeMinRef = useRef(null);
  const seriesPeMaxRef = useRef(null);
  const seriesPeMinRef = useRef(null);
  const seriesGexPeRef = useRef(null);
  const seriesDGexRef = useRef(null);
  const seriesGcashRef = useRef(null);
  const seriesWcashRef = useRef(null);
  const seriesOIRef = useRef(null);
  const seriesVolRef = useRef(null);
  const seriesDoiRef = useRef(null);
  const seriesIvRef = useRef(null);
  const seriesIvcRef = useRef(null);
  const seriesIvpRef = useRef(null);
  const seriesGdRef = useRef(null);
  const seriesGd1Ref = useRef(null);
  const seriesTgxRef = useRef(null);
  const seriesCeGxRef = useRef(null);
  const seriesPeGxRef = useRef(null);
  const seriesGdxRef = useRef(null);
  const seriesPeGdxRef = useRef(null);
  const seriesCeGdxRef = useRef(null);

  // const spotCloseRef = useRef(null);
  // const spotHighRef = useRef(null);
  // const spotLowRef = useRef(null);

  const xAxisRef = useRef(null);
  const axisGuideLabelsRef = useRef([]);
  // label -> row index (0 = Spot, 1 = Sigma, 2 = Tails)
  const guideRowRef = useRef(new Map());
  const range1Ref = useRef(null);
  const range2Ref = useRef(null);
  const range2DataItemRef = useRef(null);
  const s1ref = useRef(null);
  const s2ref = useRef(null);
  const s3ref = useRef(null);
  const s4ref = useRef(null);

  // console.log("props", props);

  const { auth } = useAuth();
  // ============================================================
  // NEW - Centralized Permission System
  // See: src/common/utility/permissionService.js
  // ============================================================
  const permissions = usePermissions();
  
  // ============================================================
  // OLD - Hardcoded user tier checks (DEPRECATED)
  // Replaced by: permissions service methods
  // See: src/common/utility/permissionService.js
  // ============================================================
  // const [isBasicUser,setIsBasicUser]=useState((auth.role.find(el=>el==USER_ROLE_FIELDS.FAST_DATA))?false:true)
  // const [isFastDataUser,setIsFastDataUser]=useState((auth.role.find(el=>el==USER_ROLE_FIELDS.FAST_DATA))?true:false)
  // const [isProUser,setIsProUser]=useState((auth.role.find(el=>el==USER_ROLE_FIELDS.PRO))?true:false)
  // const [isPro2User,setIsPro2User]=useState((auth.role.find(el=>el==USER_ROLE_FIELDS.PRO2))?true:false)
  // const [isAdminUser,setIsAdminUser]=useState((auth.role.find(el=>el==USER_ROLE_FIELDS.ADMIN))?true:false)
  
  // NEW - Use centralized permission service
  // const [isBasicUser,setIsBasicUser]=useState(permissions.isFreeUser())
  const [isFastDataUser,setIsFastDataUser]=useState(true); //useState(permissions.isPremiumUser()) // PREMIUM, PRO1, or PRO2
  const [isProUser,setIsProUser]=useState(permissions.isPremiumUser())
  // PRO2-only feature 
  const [isPro2User,setIsPro2User]=useState(permissions.isPro2User())
  const [isAdminUser,setIsAdminUser]=useState(permissions.isAdmin())
  const[nDays,setnDays]=useState(props.nDays);
  const [startDate,setStartDate]=useState(props.startDate!=""? dayjs(props.startDate,DateFormat):"");
  const[plotType,setPlotType]=useState(props.plotType);
  const[menuType,setMenuType]=useState(props.menuType);
  const [expiry,setExpiry]=useState(props.expiry?props.expiry:props.expiryList?props.expiryList[0]:"");
  const [dateParams,setDateParams]=useState(props.startDate?props.startDate:props.dateList?props.dateList[0]:"");
  const [timeMinValue,setTimeMinValue]=useState(0)
  const [timeInterval,setTimeInterval]=useState(props.timeInterval);
  const [targetTime,setTargetTime]=useState(props.targetTime)

  // console.log("+++++++", expiry, props)

  // const [spotClose,setSpotClose]=useState(props.metadata.spot)
  // const [spotHigh,setSpotHigh]=useState(props.metadata.spot_high)
  // const [spotLow,setSpotLow]=useState(props.metadata.spot_low)

  let spotClose =  props.metadata?.spot;
  let spotHigh =  props.metadata?.spot_high;
  let spotLow =  props.metadata?.spot_low;
  let atmCeIv =  props.metadata?.atm_ce_iv;
  let atmPeIv =  props.metadata?.atm_pe_iv;
  let seriesVix =  props.metadata?.series_vix;
  let s1 = props.metadata?.s1;
  let s2 = props.metadata?.s2;
  let s3 = props.metadata?.s3;
  let s4 = props.metadata?.s4;


  const d_list = ['_gcash', '_dgcash', '_igex', '_ngex', '_wcash', '_oi', '_doi', '_vol', '_iv']
  const dlist = ['net_gcash', 'net_dgcash', 'total_gcash', 'total_dgcash', 'net_igex', 'net_ngex', 'total_igex', 'total_ngex', 'pcr', 'net_oi', 'total_oi', 'straddle', 'oi_pain']
  // Strike_IV/OI → BAR_CHART_OI_PLOT_TYPE; Strike_Wcash (inventory menu) → MI + admin plot menus.
  const CATEGORICAL_ALWAYS_PLOTS = [
    ...new Set([
      '_wcash',
      'gd_curves',
      'gamma_curves',
      ...BAR_CHART_OI_PLOT_TYPE.map((item) => item.value),
      ...BAR_CHART_MI_PLOT_TYPE.map((item) => item.value),
      ...BAR_CHART_ADMIN_PLOT_TYPE.map((item) => item.value),
    ]),
  ]
  const AXIS_GUIDE_TAG = "axisGuide"
  const toCategoricalData = (data) => [...(data || [])]
    .map((item) => ({ ...item, strike: String(item.strike) }))
    .sort((a, b) => Number(a.strike) - Number(b.strike))
  const showAxisGuideLabel = (label) => {
    if (!label) return;
    label.setAll({
      visible: true,
      forceHidden: false,
    });
  };
  // row: 0 = Spot, 1 = Sigma lines, 2 = Tail lines
  const registerAxisGuideLabel = (label, row = 0) => {
    if (!label) return label;
    guideRowRef.current.set(label, row);
    const tags = label.get("themeTags") || [];
    if (!tags.includes(AXIS_GUIDE_TAG)) {
      label.set("themeTags", [...tags, AXIS_GUIDE_TAG]);
    }
    showAxisGuideLabel(label);
    if (!axisGuideLabelsRef.current.includes(label)) {
      axisGuideLabelsRef.current.push(label);
    }
    return label;
  };
  const refreshAxisGuideLabels = () => {
    axisGuideLabelsRef.current.forEach(showAxisGuideLabel);
    showAxisGuideLabel(range2DataItemRef.current?.get("label"));
  };
  // put a guide line (and its label) at an exact position inside its category cell
  const setGuideLocation = (dataItem, loc) => {
    if (!dataItem) return;
    dataItem.get("grid")?.set("location", loc);
    dataItem.get("label")?.set("location", loc);
  };

  // console.log("-------menuType", menuType)
  let plot = props.plotParams.get("plot");
  let plot_name = plot;
  let green_color = "#01DF01";
  let red_color = "#FF0000";
  if(plot == "net_gcash" || plot == "total_gcash" || plot == "_gcash"){
    plot_name = plot.split("_")[0].charAt(0).toUpperCase() + plot.split("_")[0].slice(1) + " Gamma Exposure Value";
    green_color = "#6DD9E5";
    red_color = "#CC80F9";
    if(plot == "net_gcash"){
      green_color = "#CC80F9";
    }
  }
  else if(plot == "net_igex" || plot == "total_igex" || plot == "_igex"){
    plot_name = plot.split("_")[0].charAt(0).toUpperCase() + plot.split("_")[0].slice(1) + " Gamma Value";
    green_color = "#6DD9E5";
    red_color = "#CC80F9";
    if(plot == "net_gcash"){
      green_color = "#CC80F9";
    }
  }
  else if(plot == "net_dgcash" || plot == "total_dgcash" || plot == "_dgcash"){
    plot_name = plot.split("_")[0].charAt(0).toUpperCase() + plot.split("_")[0].slice(1) + " Inventory Resistance Estimate";
    green_color = "#6DD9E5";
    red_color = "#CC80F9";
    if(plot == "net_dgcash"){
      green_color = "#CC80F9";
    }
  }
  else if(plot == "net_ngex" || plot == "total_ngex" || plot == "_ngex"){
    plot_name = plot.split("_")[0].charAt(0).toUpperCase() + plot.split("_")[0].slice(1) + " Inventory Value";
    green_color = "#6DD9E5";
    red_color = "#CC80F9";
    if(plot == "net_dgcash"){
      green_color = "#CC80F9";
    }
  }
  else if(plot == "_wcash")
    plot_name = "Writers' Unrealized Cash"
  // console.log(props)

  // const theme = useTheme();
  const navigate = useNavigate();

  // console.log("CONTAINER BEGINS")
  // console.log(props)

 
useEffect(()=>{
  console.log("Bar chart: target time useEffect= ",props.targetTime);
  if(props.targetTime!=undefined)
  setTargetTime(props.targetTime)
},[props.targetTime])


  useLayoutEffect(() => {
    if(props.isError)
    return;

    axisGuideLabelsRef.current = [];
    guideRowRef.current = new Map();
    
    let root = am5.Root.new(props.id, {
      // useSafeResolution: false  TODO: itest incase charts break on iOS device uncomment it
    });

    root.setThemes([dark_theme.new(root)]);
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        // wheelX: "zoomX",
        wheelZoomPositionX: 1,
        pinchZoomX: false,
        pinchZoomY: false,
        paddingTop: 20,
        paddingRight: 15,
        paddingBottom: 30,
        paddingLeft: 25,
        layout: root.verticalLayout
      })
    );

    //Refere for zoom and pan https://www.amcharts.com/docs/v5/charts/xy-chart/zoom-and-pan/

    // uncomment the below line is to hide the chart zoom button
    // chart.zoomOutButton.set("forceHidden", true); 

    // add scrollbar to control
    // chart.set("scrollbarX", am5.Scrollbar.new(root, {
    //   orientation: "horizontal"
    // }));

    const isCategoricalX = CATEGORICAL_ALWAYS_PLOTS.includes(plot);
    const xBinding = isCategoricalX
      ? { categoryXField: "strike" }
      : { valueXField: "strike" };
    // Category X: multiple ColumnSeries at one strike stack by default → Y looks multiplied (CE+PE+max+min).
    // Data fields are unchanged; only tell amCharts not to sum columns.
    const categoricalSplitBars = isCategoricalX
      ? { stacked: false, clustered: true }
      : { clustered: true };
    const categoricalOverlayBars = isCategoricalX
      ? { stacked: false, clustered: false }
      : {};
    const categoryChartData = toCategoricalData(props.chartData);
    const nearestStrikeCategory = (value) => getNearestStrike(value, categoryChartData);

    let rendrerStrikes = am5xy.AxisRendererX.new(root, isCategoricalX ? {
      minGridDistance: 40,
      cellStartLocation: 0.2,
      cellEndLocation: 0.8
    } : {});
    rendrerStrikes.grid.template.setAll({
      location: isCategoricalX ? 0.5 : 0,
      disabled: false,
      cellStartLocation: 0.2,
      cellEndLocation: 0.8
    });
    rendrerStrikes.ticks.template.setAll({
      length: 8,
      strokeOpacity: 0.2,
      disabled: false,
    });
    rendrerStrikes.labels.template.setAll(isCategoricalX
      ? {
          textAlign: "center",
          paddingLeft: 4,
          paddingRight: 4
        }
      : {
          minPosition: 0.01,
          maxPosition: 0.99,
        }
    );

    //create strike axis
    // startLocation 0 / endLocation 1 -> first and last strike sit half a cell
    // inside the plot, so their labels are never clipped at the edges
    let strikesAxis = isCategoricalX
      ? am5xy.CategoryAxis.new(root, {
          categoryField: "strike",
          startLocation: 0,
          endLocation: 1,
          renderer: rendrerStrikes
        })
      : am5xy.ValueAxis.new(root, {
          height: am5.percent(100),
          renderer: rendrerStrikes
        });

    let xAxis = chart.xAxes.push(
      strikesAxis
    );

    if (isCategoricalX) {
      xAxis.data.setAll(categoryChartData);
    }

    if (isCategoricalX) {
      const strikeNumbers = categoryChartData.map((item) => Number(item.strike));
      const strikeUnit = getStrikeUnit(categoryChartData);
      let labelStrikeBase = strikeNumbers[0] ?? 0;
      let labelPriceStep = strikeUnit * 2;
      let labelMinPx = 76;

      const updateLabelPriceStep = () => {
        const axisWidth = xAxis.width() || chart.plotContainer.width();
        if (!axisWidth || strikeNumbers.length < 1) return;
        labelStrikeBase = strikeNumbers[0];
        const pixelsPerSlot = axisWidth / strikeNumbers.length;
        const slotsPerLabel = Math.max(1, Math.ceil(labelMinPx / pixelsPerSlot));
        labelPriceStep = strikeUnit * slotsPerLabel;
      };

      const shouldShowStrikeLabel = (category) => {
        const strike = Number(category);
        if (!Number.isFinite(strike)) return false;
        if (strike === strikeNumbers[0] || strike === strikeNumbers[strikeNumbers.length - 1]) {
          return true;
        }
        const offset = strike - labelStrikeBase;
        return offset % labelPriceStep === 0;
      };

      // Set forceHidden directly on every strike label. An adapter is not re-evaluated
      // when the chart is resized (multi-chart mode), which left too many labels visible.
      const applyStrikeLabelVisibility = () => {
        xAxis.dataItems.forEach((dataItem) => {
          const label = dataItem.get("label");
          if (!label) return;
          const tags = label.get("themeTags");
          if (Array.isArray(tags) && tags.includes(AXIS_GUIDE_TAG)) return;
          if (dataItem.get("endCategory") != null) return;
          const category = dataItem.get("category");
          if (category == null) return;
          label.set("forceHidden", !shouldShowStrikeLabel(category));
        });
      };

      // Guide labels (Spot / Sigma / Tail) are placed inside the plot, each kind on its
      // own row so they never overlap:
      //   row 0 = Spot (may be multi-line), row 1 = Sigma, row 2 = Tails
      // Spot sits 5px under the top of the plot. Later rows keep the same gaps as before.
      const SPOT_TOP_GAP = 5;
      const positionGuideLabelsInPlot = () => {
        const plotHeight = chart.plotContainer.height();
        if (!plotHeight) return;
        const topDy = -(plotHeight - SPOT_TOP_GAP);
        const guideLabels = [...axisGuideLabelsRef.current];
        const spotLabel = range2DataItemRef.current?.get("label");
        if (spotLabel && !guideLabels.includes(spotLabel)) {
          guideLabels.push(spotLabel);
        }
        const spotHeight = Math.max(spotLabel?.height?.() || 0, 22);
        guideLabels.forEach((label) => {
          const row = guideRowRef.current.get(label) ?? 0;
          const extra = row === 0 ? 0 : spotHeight + 8 + (row - 1) * GUIDE_ROW_GAP;
          label.setAll({
            inside: true,
            centerY: 0,
            paddingTop: 0,
            dy: topDy + extra,
            dx: 8,
            visible: true,
            forceHidden: false,
          });
        });
      };

      // CE / PE are the only clustered series (2 slots per strike). The intra Max/Min boxes
      // are not clustered, so they sit at the cell centre; shift each onto its own bar.
      const positionIntraSeries = () => {
        if (plot !== "_oi" && plot !== "_igex") return;
        const axisWidth = xAxis.width();
        const span = (xAxis.get("end") - xAxis.get("start")) || 1;
        if (!axisWidth || strikeNumbers.length < 1) return;
        const cellPx = axisWidth / span / strikeNumbers.length;
        const shift = cellPx * 0.15; // slot centre offset: (cellEnd - cellStart) / 4
        [[seriesCeMax, -shift], [seriesCeMin, -shift], [seriesPeMax, shift], [seriesPeMin, shift]]
          .forEach(([s, dx]) => {
            if (s) s.columns.template.set("dx", dx);
          });
      };

      const applyStrikeLabelLayout = () => {
        updateLabelPriceStep();
        positionIntraSeries();
        applyStrikeLabelVisibility();
        refreshAxisGuideLabels();
        positionGuideLabelsInPlot();
        xAxis.markDirty();
      };
      const scheduleStrikeLabelLayout = () => {
        root.events.once("frameended", applyStrikeLabelLayout);
      };
      updateLabelPriceStep();
      xAxis.events.on("datavalidated", scheduleStrikeLabelLayout);
      chart.plotContainer.events.on("boundschanged", scheduleStrikeLabelLayout);
    }

    let valueAxisLhs1 = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        height: am5.percent(100),
        renderer: am5xy.AxisRendererY.new(root, {
          opposite: true,
        }),
        visible: false
      })
    );

    let valueAxisGdRhs1 = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        height: am5.percent(100),
        renderer: am5xy.AxisRendererY.new(root, {
          opposite: true,
        }),
        visible: false
      })
    );

    let valueAxisRhs1 = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        // syncWithAxis: valueAxisLhs1,
        height: am5.percent(100),
        renderer: am5xy.AxisRendererY.new(root, {
          opposite: true,
        }),
      })
    );

    let valueAxisRhs2 = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        syncWithAxis: valueAxisGdRhs1,
        height: am5.percent(100),
        renderer: am5xy.AxisRendererY.new(root, {
          opposite: true,
        }),
        visible: false
      })
    );

    let seriesGex, seriesGexMax, seriesGexMin;
    if(dlist.includes(plot)) {
      seriesGex = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: plot_name,
          xAxis: xAxis,
          ...(isCategoricalX ? {} : { centerX: 10 }),
          // syncWithAxis: valueAxisRhs1,
          yAxis: valueAxisRhs1,
          valueYField: plot,
          ...xBinding,
          ...categoricalOverlayBars,
          tooltip: am5.Tooltip.new(root, {})
        })
      );

      seriesGex.columns.template.setAll({
        fillOpacity: 0.9,
        strokeWidth: 0,
        strokeOpacity: 0,
        minWidth: 20,
        maxWidth: 20,
        width: am5.percent(100)
      });

      seriesGex.columns.template.adapters.add("fill", function(fill, target) {
        if (target.dataItem && target.dataItem.get("valueY") > 0) {
            return am5.color(green_color);  // green
        }
        else {
            return am5.color(red_color);  // red
        }
      });

      seriesGex.get("tooltip").label.set("text", plot_name + ": {valueY}")

      seriesGexMax = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: plot_name + " Max (Intra)",
          xAxis: xAxis,
          ...(isCategoricalX ? {} : { centerX: 15 }),
          // syncWithAxis: valueAxisRhs1,
          yAxis: valueAxisRhs1,
          valueYField: plot + "_max",
          openValueYField: plot + "_min",
          ...xBinding,
          ...categoricalOverlayBars,
          stroke: am5.color("#FFFFFF"),
          fill: am5.color("#B9F6FE"),
          // tooltip: am5.Tooltip.new(root, {})
        })
      );

      seriesGexMax.columns.template.setAll({
        fillOpacity: 0.3,
        strokeWidth: 0.5,
        strokeOpacity: 0.8,
        minWidth: 22,
        maxWidth: 22,
        width: am5.percent(100)
      });

      // seriesGexMax.get("tooltip").label.set("text", plot_name + " Max: {valueY}")

      seriesGexMin = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: plot_name + " Min (Intra)",
          xAxis: xAxis,
          // centerX: 10,
          // syncWithAxis: valueAxisRhs1,
          yAxis: valueAxisRhs1,
          valueYField: plot + "_min",
          ...xBinding,
          ...categoricalOverlayBars,
          // stroke: am5.color("#CE40FB"),
          // tooltip: am5.Tooltip.new(root, {})
        })
      );

      seriesGexMin.columns.template.setAll({
        fillOpacity: 0,
        strokeWidth: 0,
        strokeOpacity: 0,
        minWidth: 22,
        maxWidth: 22,
        width: am5.percent(100)
      });

      // seriesGexMin.get("tooltip").label.set("text", plot_name + " Min: {valueY}")
    }

    let seriesGexCe, seriesGexPe;
    let seriesCeMin, seriesPeMin, seriesCeMax, seriesPeMax;
    if(d_list.includes(plot)) {
    // if(props.plotParams.get("plot") === "_gex") {
      seriesGexCe = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: "CE " + plot_name,
          xAxis: xAxis,
          // syncWithAxis: valueAxisRhs1,
          yAxis: valueAxisRhs1,
          valueYField: "ce" + plot,
          ...xBinding,
          ...categoricalSplitBars,
          tooltip: am5.Tooltip.new(root, {})
        })
      );

      seriesGexCe.columns.template.setAll({
        fillOpacity: 0.6,
        strokeWidth: 0,
        strokeOpacity: 0,
        minWidth: 10,
        // maxWidth: 40,
        // width: am5.percent(50),
        // height: am5.percent(80),
        stroke: am5.color(green_color),
        fill: am5.color(green_color),
      });

      seriesGexCe.get("tooltip").label.set("text", "ce: {valueY}")

      seriesGexPe = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: "PE " + plot_name,
          xAxis: xAxis,
          ...(isCategoricalX ? {} : { centerX: 15 }),
          // syncWithAxis: valueAxisRhs1,
          yAxis: valueAxisRhs1,
          valueYField: "pe" + plot,
          ...xBinding,
          ...categoricalSplitBars,
          tooltip: am5.Tooltip.new(root, {})
        })
      );

      seriesGexPe.columns.template.setAll({
        fillOpacity: 0.6,
        strokeWidth: 0,
        strokeOpacity: 0,
        minWidth: 10,
        // maxWidth: 40,
        // width: am5.percent(50),
        // height: am5.percent(80),
        stroke: am5.color(red_color),
        fill: am5.color(red_color),
      });

      seriesGexPe.get("tooltip").label.set("text", "pe: {valueY}")
      
      if(plot == "_oi") {
        seriesCeMax = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "CE OI Max (Intra)",
            xAxis: xAxis,
            ...(isCategoricalX ? {} : { centerX: 0 }),
            // syncWithAxis: valueAxisRhs1,
            yAxis: valueAxisRhs1,
            valueYField:  "ce_oi_max",
            openValueYField: "ce_oi_min",
            ...xBinding,
            ...categoricalOverlayBars,
            stroke: am5.color("#FFFFFF"),
            fill: am5.color("#B9F6FE"),
            tooltip: am5.Tooltip.new(root, {})
          })
        );
  
        seriesCeMax.columns.template.setAll({
          fillOpacity: 0.2,
          strokeWidth: 0.5,
          strokeOpacity: 0.8,
          minWidth: 10,
          maxWidth: 10,
          width: am5.percent(100)
        });
  
        seriesCeMax.get("tooltip").label.set("text", "ce Max: {valueY}")
  
        seriesCeMin = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "CE OI Min (Intra)",
            xAxis: xAxis,
            // centerX: 10,
            // syncWithAxis: valueAxisRhs1,
            yAxis: valueAxisRhs1,
            valueYField: "ce_oi_min",
            ...xBinding,
            ...categoricalOverlayBars,
            // stroke: am5.color("#CE40FB"),
            // tooltip: am5.Tooltip.new(root, {})
          })
        );
  
        seriesCeMin.columns.template.setAll({
          fillOpacity: 0,
          strokeWidth: 0,
          strokeOpacity: 0,
          minWidth: 10,
          maxWidth: 10,
          width: am5.percent(100)
        });

        seriesPeMax = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "PE OI Max (Intra)",
            xAxis: xAxis,
            ...(isCategoricalX ? {} : { centerX: 15 }),
            // syncWithAxis: valueAxisRhs1,
            yAxis: valueAxisRhs1,
            valueYField:  "pe_oi_max",
            openValueYField: "pe_oi_min",
            ...xBinding,
            ...categoricalOverlayBars,
            stroke: am5.color("#FFFFFF"),
            fill: am5.color("#B9F6FE"),
            tooltip: am5.Tooltip.new(root, {})
          })
        );
  
        seriesPeMax.columns.template.setAll({
          fillOpacity: 0.2,
          strokeWidth: 0.5,
          strokeOpacity: 0.8,
          minWidth: 10,
          maxWidth: 10,
          width: am5.percent(100)
        });
  
        seriesPeMax.get("tooltip").label.set("text", "pe Max: {valueY}")
  
        seriesPeMin = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "PE OI Min (Intra)",
            xAxis: xAxis,
            // centerX: 10,
            // syncWithAxis: valueAxisRhs1,
            yAxis: valueAxisRhs1,
            valueYField: "pe_oi_min",
            ...xBinding,
            ...categoricalOverlayBars,
            // stroke: am5.color("#CE40FB"),
            // tooltip: am5.Tooltip.new(root, {})
          })
        );
  
        seriesPeMin.columns.template.setAll({
          fillOpacity: 0,
          strokeWidth: 0,
          strokeOpacity: 0,
          minWidth: 10,
          maxWidth: 10,
          width: am5.percent(100)
        });
      }

      if(plot == "_igex") {
        seriesCeMax = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "CE Gamma Max (Intra)",
            xAxis: xAxis,
            ...(isCategoricalX ? {} : { centerX: 0 }),
            // syncWithAxis: valueAxisRhs1,
            yAxis: valueAxisRhs1,
            valueYField:  "ce_igex_max",
            openValueYField: "ce_igex_min",
            ...xBinding,
            ...categoricalOverlayBars,
            stroke: am5.color("#FFFFFF"),
            fill: am5.color("#B9F6FE"),
            tooltip: am5.Tooltip.new(root, {})
          })
        );
  
        seriesCeMax.columns.template.setAll({
          fillOpacity: 0.2,
          strokeWidth: 0.5,
          strokeOpacity: 0.8,
          minWidth: 10,
          maxWidth: 10,
          width: am5.percent(100)
        });
  
        seriesCeMax.get("tooltip").label.set("text", "ce Max: {valueY}")
  
        seriesCeMin = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "CE Gamma Min (Intra)",
            xAxis: xAxis,
            // centerX: 10,
            // syncWithAxis: valueAxisRhs1,
            yAxis: valueAxisRhs1,
            valueYField: "ce_igex_min",
            ...xBinding,
            ...categoricalOverlayBars,
            // stroke: am5.color("#CE40FB"),
            // tooltip: am5.Tooltip.new(root, {})
          })
        );
  
        seriesCeMin.columns.template.setAll({
          fillOpacity: 0,
          strokeWidth: 0,
          strokeOpacity: 0,
          minWidth: 10,
          maxWidth: 10,
          width: am5.percent(100)
        });

        seriesPeMax = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "PE Gamma Max (Intra)",
            xAxis: xAxis,
            ...(isCategoricalX ? {} : { centerX: 15 }),
            // syncWithAxis: valueAxisRhs1,
            yAxis: valueAxisRhs1,
            valueYField:  "pe_igex_max",
            openValueYField: "pe_igex_min",
            ...xBinding,
            ...categoricalOverlayBars,
            stroke: am5.color("#FFFFFF"),
            fill: am5.color("#B9F6FE"),
            tooltip: am5.Tooltip.new(root, {})
          })
        );
  
        seriesPeMax.columns.template.setAll({
          fillOpacity: 0.2,
          strokeWidth: 0.5,
          strokeOpacity: 0.8,
          minWidth: 10,
          maxWidth: 10,
          width: am5.percent(100)
        });
  
        seriesPeMax.get("tooltip").label.set("text", "pe Max: {valueY}")
  
        seriesPeMin = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "PE Gamma Min (Intra)",
            xAxis: xAxis,
            // centerX: 10,
            // syncWithAxis: valueAxisRhs1,
            yAxis: valueAxisRhs1,
            valueYField: "pe_igex_min",
            ...xBinding,
            ...categoricalOverlayBars,
            // stroke: am5.color("#CE40FB"),
            // tooltip: am5.Tooltip.new(root, {})
          })
        );
  
        seriesPeMin.columns.template.setAll({
          fillOpacity: 0,
          strokeWidth: 0,
          strokeOpacity: 0,
          minWidth: 10,
          maxWidth: 10,
          width: am5.percent(100)
        });
      }
    }

    let seriesIv, seriesIvp, seriesIvc;
    if(props.plotParams.get("plot") === "iv") {
      seriesIv = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: "IV",
          xAxis: xAxis,
          zIndex: 3,
          // syncWithAxis: valueAxisRhs1,
          yAxis: valueAxisRhs1,
          stroke: am5.color("#FF9933"),
          // fill: am5.color("#FF9933"),
          valueYField: "iv",
          ...xBinding,
          // tooltip: am5.Tooltip.new(root, {})
        })
      );

      seriesIv.strokes.template.setAll({
        strokeWidth: 2,
      });
      // seriesIv.get("tooltip").label.set("text", "IV: {valueY}")

      seriesIvp = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: "Puts IV",
          xAxis: xAxis,
          zIndex: 3,
          // syncWithAxis: valueAxisRhs1,
          yAxis: valueAxisRhs1,
          stroke: am5.color("#FF0000"),
          // fill: am5.color("#FF0000"),
          valueYField: "pe_iv",
          ...xBinding,
          // tooltip: am5.Tooltip.new(root, {})
        })
      );

      seriesIvp.strokes.template.setAll({
        strokeWidth: 2,
      });

      seriesIvc = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: "Calls IV",
          xAxis: xAxis,
          zIndex: 3,
          // syncWithAxis: valueAxisRhs1,
          yAxis: valueAxisRhs1,
          stroke: am5.color("#01DF01"),
          // fill: am5.color("#01DF01"),
          valueYField: "ce_iv",
          ...xBinding,
          // tooltip: am5.Tooltip.new(root, {})
        })
      );

      seriesIvc.strokes.template.setAll({
        strokeWidth: 2,
      });
    }

    // row: 1 = Sigma lines, 2 = Tail lines (Spot is row 0)
    function createSpotLine(value, color, labelText, row = 1) {
      let spotDataItem = xAxis.makeDataItem(isCategoricalX
        ? { category: nearestStrikeCategory(value) }
        : { value: value }
      );
      // let dashArray = [2, 2];

      let spot = xAxis.createAxisRange(spotDataItem);

      spot.get("grid").setAll({
        stroke: am5.color(color),
        strokeOpacity: 1,
        strokeWidth: 2,
        strokeDasharray: [2, 2]
      });

      // exact position inside the strike cell (no snapping to the nearest strike)
      if (isCategoricalX) {
        setGuideLocation(spotDataItem, getCategoryLocation(value, categoryChartData));
      }
      
      registerAxisGuideLabel(spotDataItem.get("label"), row).setAll({
        fill: am5.color(color),
        text: labelText + ": " + value,
        inside: true,
        visible: true,
        forceHidden: false,
        dx: 8,
        // background: am5.RoundedRectangle.new(root, {
        //   fill: am5.color("#FFFFFF"),
        // })
      });

      return spot;
    }

    let seriesGd, seriesGd1, seriesGdx, seriesPeGdx, seriesCeGdx;
    if(props.plotParams.get("plot") === "gd_curves") {
      seriesGd = chart.series.push(
        am5xy.SmoothedXLineSeries.new(root, {
          name: "Density",
          xAxis: xAxis,
          yAxis: valueAxisRhs2,
          stroke: am5.color("#FF9933"),
          valueYField: "gd",
          ...xBinding,
        })
      );
      seriesGd.strokes.template.setAll({
        strokeWidth: 2,
      });
      seriesGd.bullets.push(function (root) {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 4,
            fill: am5.color("#FF9933"),
          })
        });
      });

      // seriesGd1 = chart.series.push(
      //   am5xy.SmoothedXLineSeries.new(root, {
      //     name: "Clipped Density",
      //     xAxis: xAxis,
      //     yAxis: valueAxisRhs2,
      //     stroke: am5.color("#c6a481"),
      //     valueYField: "gd1",
      //     ...xBinding,
      //   })
      // );
      // seriesGd1.strokes.template.setAll({
      //   strokeWidth: 1,
      //   strokeDasharray: [3, 2]
      // });

      seriesGdx = chart.series.push(
        am5xy.SmoothedXLineSeries.new(root, {
          name: "Convexity Zone",
          xAxis: xAxis,
          yAxis: valueAxisGdRhs1,
          stroke: am5.color("#ddfa94"),
          valueYField: "gdx",
          ...xBinding,
        })
      );
      seriesGdx.strokes.template.setAll({
        strokeWidth: 2,
      });
      seriesGdx.bullets.push(function (root) {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 4,
            fill: am5.color("#ddfa94"),
          })
        });
      });

      // seriesPeGdx = chart.series.push(
      //   am5xy.SmoothedXLineSeries.new(root, {
      //     name: "BS_PDF Sell",
      //     xAxis: xAxis,
      //     yAxis: valueAxisGdRhs1,
      //     stroke: am5.color("#e66565"),
      //     valueYField: "pe_gdx",
      //     ...xBinding,
      //   })
      // );
      // seriesPeGdx.strokes.template.setAll({
      //   strokeWidth: 2,
      // });

      // seriesCeGdx = chart.series.push(
      //   am5xy.SmoothedXLineSeries.new(root, {
      //     name: "BS_PDF Buy",
      //     xAxis: xAxis,
      //     yAxis: valueAxisGdRhs1,
      //     stroke: am5.color("#41ab41"),
      //     valueYField: "ce_gdx",
      //     ...xBinding,
      //   })
      // );
      // seriesCeGdx.strokes.template.setAll({
      //   strokeWidth: 2,
      // });

      createSpotLine(s1, "#ECF539", "Lower Tail", 2);
      createSpotLine(s2, "#a290d3", "-1 Sigma", 1);
      createSpotLine(s3, "#f09438", "+1 Sigma", 1);
      createSpotLine(s4, "#e05454", "Upper Tail", 2);
    }

    let seriesTgx, seriesCeGx, seriesPeGx
    if(props.plotParams.get("plot") === "gamma_curves") {
      seriesTgx = chart.series.push(
        am5xy.SmoothedXLineSeries.new(root, {
          name: "Neutral Exposure",
          xAxis: xAxis,
          yAxis: valueAxisRhs1,
          stroke: am5.color("#e3e3c2"),
          valueYField: "tgx",
          ...xBinding,
        })
      );
      seriesTgx.strokes.template.setAll({
        strokeWidth: 2,
      });

      seriesTgx.bullets.push(function(root) {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 4,
            fill: am5.color("#e3e3c2"),
          })
        });
      });

      seriesCeGx = chart.series.push(
        am5xy.SmoothedXLineSeries.new(root, {
          name: "Buy Exposure",
          xAxis: xAxis,
          yAxis: valueAxisRhs1,
          stroke: am5.color("#348d47"),
          valueYField: "cegx",
          ...xBinding,
        })
      );
      seriesCeGx.strokes.template.setAll({
        strokeWidth: 2,
      });

      seriesCeGx.bullets.push(function(root) {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 4,
            fill: am5.color("#348d47"),
          })
        });
      });

      seriesPeGx = chart.series.push(
        am5xy.SmoothedXLineSeries.new(root, {
          name: "Sell Exposure",
          xAxis: xAxis,
          yAxis: valueAxisRhs1,
          stroke: am5.color("#d0501e"),
          valueYField: "pegx",
          ...xBinding,
        })
      );
      seriesPeGx.strokes.template.setAll({
        strokeWidth: 2,
      });

      seriesPeGx.bullets.push(function(root) {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 4,
            fill: am5.color("#d0501e"),
          })
        });
      });
    }

    // ---- shaded band: spot low .. spot high ----
    let range1DataItem = xAxis.makeDataItem(isCategoricalX
      ? {
          category: nearestStrikeCategory(spotLow),
          endCategory: nearestStrikeCategory(spotHigh),
          categoryLocation: getCategoryLocation(spotLow, categoryChartData),
          endCategoryLocation: getCategoryLocation(spotHigh, categoryChartData),
        }
      : {
          value: spotLow,
          endValue: spotHigh,
        }
    );

    let range1 = xAxis.createAxisRange(range1DataItem);

    // range1.get("grid").setAll({
    //   stroke: am5.color("#FFDD33"),
    //   strokeOpacity: 1,
    // });

    range1DataItem.get("axisFill").setAll({
      fill: am5.color("#f8f0c6"),
      fillOpacity: 0.3,
      visible: true
    });

    // The band's own axis label was drawn at the middle of the band and showed a stray
    // strike number (e.g. "22650") on the x axis, overlapping the real labels. Hide it.
    const range1Label = range1DataItem.get("label");
    if (range1Label) {
      range1Label.setAll({ text: "", visible: false, forceHidden: true });
    }

    // ---- spot line ----
    let range2DataItem = xAxis.makeDataItem(isCategoricalX
      ? { category: nearestStrikeCategory(spotClose) }
      : { value: spotClose }
    );

    let range2 = xAxis.createAxisRange(range2DataItem);

    range2.get("grid").setAll({
      stroke: am5.color("#FFFFFF"),
      strokeOpacity: 1,
      strokeWidth: 2,
    });

    if (isCategoricalX) {
      setGuideLocation(range2DataItem, getCategoryLocation(spotClose, categoryChartData));
    }

    registerAxisGuideLabel(range2DataItem.get("label"), 0).setAll({
      fill: am5.color("#ECF539"),
      text: "Spot: " + spotClose,
      inside: true,
      visible: true,
      forceHidden: false,
      dx: 8,
      // background: am5.RoundedRectangle.new(root, {
      //   fill: am5.color("#FFFFFF"),
      // })
    });

    // Stack Y-axes
    chart.leftAxesContainer.set("layout", root.verticalLayout);
    chart.rightAxesContainer.set("layout", root.verticalLayout);

    seriesGexRef.current = seriesGex;
    seriesGexMaxRef.current = seriesGexMax;
    seriesGexMinRef.current = seriesGexMin;
    seriesGexCeRef.current = seriesGexCe;
    seriesCeMaxRef.current = seriesCeMax;
    seriesCeMinRef.current = seriesCeMin;
    seriesPeMaxRef.current = seriesPeMax;
    seriesPeMinRef.current = seriesPeMin;
    seriesGexPeRef.current = seriesGexPe;
    seriesIvRef.current = seriesIv;
    seriesIvcRef.current = seriesIvc;
    seriesIvpRef.current = seriesIvp;
    seriesGdRef.current = seriesGd;
    seriesGd1Ref.current = seriesGd1;
    seriesTgxRef.current = seriesTgx;
    seriesCeGxRef.current = seriesCeGx;
    seriesPeGxRef.current = seriesPeGx;
    seriesGdxRef.current = seriesGdx;
    seriesPeGdxRef.current = seriesPeGdx;
    seriesCeGdxRef.current = seriesCeGdx;

    // spotCloseRef.current = spotClose;
    // spotHighRef.current = spotHigh;
    // spotLowRef.current = spotLow;

    xAxisRef.current = xAxis;
    range1Ref.current = range1;
    range2Ref.current = range2;
    range2DataItemRef.current = range2DataItem;

    s1ref.current = s1;
    s2ref.current = s2;
    s3ref.current = s3;
    s4ref.current = s4;

    //create chart legend
    let legend = chart.children.push(am5.Legend.new(root, {
      centerX: am5.percent(50),
      x: am5.percent(50),
      marginTop:20
    }));
    legend.data.setAll(chart.series.values);

    // //create chart title
    // chart.children.unshift(am5.Label.new(root, {
    //   text: "NIFTY Futures Line Chart",
    //   fontSize: 24,
    //   fontWeight: "500",
    //   textAlign: "center",
    //   x: am5.percent(50),
    //   centerX: am5.percent(50),
    //   paddingTop: 0,
    //   paddingBottom: 4
    // }));

    //add watermark text
    chart.plotContainer.children.push(am5.Label.new(root, {
      text: WATERMARK_CONFIG.LC_TEXT,
      y: am5.percent(55),
      x: am5.percent(37),
      centerY: am5.percent(100),
      opacity: 0.3,
      marginLeft: 10,
      marginBottom: 10,
      fontSize: 28,
      fontWeight: "400",
    }));

    chart.plotContainer.get("background").setAll({
      stroke: am5.color("#000000"),
      strokeOpacity: 0.5,
      fill: am5.color("#000000"),
      fillOpacity: 0.7
    });

    // Add cursor for x axis
    const cursor = am5xy.XYCursor.new(root, {
      behavior: "zoomXY",
      xAxis: xAxis
    });
    chart.set("cursor", cursor);

    //add tooltip for x axis
    xAxis.set("tooltip", am5.Tooltip.new(root, {
      themeTags: ["axis"]
    }));

    strikesAxis.set("tooltip", am5.Tooltip.new(root, {
      themeTags: ["axis"]
    }));

    if (plot === "gd_curves") {
      // Gamma Density has no Y value on the cursor; the NaN line looks bad
      if (cursor.lineY) {
        cursor.lineY.set("visible", false);
      }
    } else {
      valueAxisRhs1.set("tooltip", am5.Tooltip.new(root, {
        themeTags: ["axis"]
      }));
    }

    return () => {
      root.dispose();
    };

  }, [props.chartData]);

  //this will handle the props data change and set/update the chart
  useLayoutEffect(() => {
    // console.log("uselayout effect child ===========>>>>>>>>>>>>>>>");
    //1st load
    if(props.isError)
    return;
    if (props.chartData != undefined) {
      console.log("initial chart load");
      // console.log(range1Ref.current);
      if(props.metadata != undefined && range1Ref.current != undefined){
        // spotCloseRef.current = props.metadata.spot;
        // spotHighRef.current = props.metadata.spot_high;
        // spotLowRef.current = props.metadata.spot_low;

        const currentPlot = props.plotParams.get("plot");
        const isCategoricalX = CATEGORICAL_ALWAYS_PLOTS.includes(currentPlot);
        if (isCategoricalX) {
          const data = props.chartData;
          setSpotBandRange(
            range1Ref.current,
            props.metadata.spot_low,
            props.metadata.spot_high,
            data
          );
          range2Ref.current.set("category", getNearestStrike(props.metadata.spot, data) ?? "");
          // keep the spot line at the exact spot value
          setGuideLocation(range2Ref.current, getCategoryLocation(props.metadata.spot, data));
          // keep the band's own label hidden (it showed a stray strike number)
          range1Ref.current.get("label")?.setAll({ text: "", visible: false, forceHidden: true });
        } else {
          range1Ref.current._settings.value = props.metadata.spot_low;
          range1Ref.current._settings.endValue = props.metadata.spot_high;
          range2Ref.current._settings.value = props.metadata.spot;
        }
        s1ref.current = props.metadata.s1;
        s2ref.current = props.metadata.s2;
        s3ref.current = props.metadata.s3;
        s4ref.current = props.metadata.s4;
        
        const spotLabel = range2DataItemRef.current?.get("label");
        if (spotLabel) {
          registerAxisGuideLabel(spotLabel, 0);
          spotLabel.setAll(seriesVix ? {
            text: "Spot: " + props.metadata.spot + "\nSeries VIX: " + seriesVix + "\nATM PE IV: " + atmPeIv + "\nATM CE IV: " + atmCeIv,
            visible: true,
            forceHidden: false,
          } : {
            text: "Spot: " + props.metadata.spot,
            visible: true,
            forceHidden: false,
          });
        }
        refreshAxisGuideLabels();

        // console.log(range1Ref.current);
      }
      const currentPlot = props.plotParams.get("plot");
      const isCategoricalData = CATEGORICAL_ALWAYS_PLOTS.includes(currentPlot);
      const chartDataForAxis = isCategoricalData ? toCategoricalData(props.chartData) : props.chartData;
      if (isCategoricalData) {
        xAxisRef.current?.data.setAll(chartDataForAxis);
      }

      if(dlist.includes(currentPlot) && seriesGexRef.current != undefined && seriesGexRef.current.data != undefined) {
        seriesGexRef.current.data.setAll(chartDataForAxis);
        // console.log("target Time", targetTime);
        seriesGexMaxRef.current.data.setAll(chartDataForAxis);
        seriesGexMinRef.current.data.setAll(chartDataForAxis);
      } else if(d_list.includes(currentPlot) && seriesGexCeRef.current != undefined && seriesGexCeRef.current.data != undefined) {
        seriesGexCeRef.current.data.setAll(chartDataForAxis);
        seriesGexPeRef.current.data.setAll(chartDataForAxis);
        if(["_oi","_igex"].includes(currentPlot) && seriesCeMaxRef.current?.data != undefined && seriesPeMaxRef.current?.data != undefined) {
          seriesCeMaxRef.current.data.setAll(chartDataForAxis);
          seriesCeMinRef.current.data.setAll(chartDataForAxis);
          seriesPeMaxRef.current.data.setAll(chartDataForAxis);
          seriesPeMinRef.current.data.setAll(chartDataForAxis);
        }
      } else if(currentPlot == "iv" && seriesIvRef.current != undefined && seriesIvRef.current.data != undefined) {
        seriesIvRef.current.data.setAll(chartDataForAxis);
        seriesIvpRef.current.data.setAll(chartDataForAxis);
        seriesIvcRef.current.data.setAll(chartDataForAxis);
      }
      else if(currentPlot== "gd_curves" && seriesGdRef.current != undefined && seriesGdRef.current.data != undefined) {
        seriesGdRef.current.data.setAll(chartDataForAxis);
        // seriesGd1Ref.current.data.setAll(props.chartData);
        seriesGdxRef.current.data.setAll(chartDataForAxis);
        // seriesPeGdxRef.current.data.setAll(props.chartData);
        // seriesCeGdxRef.current.data.setAll(props.chartData);
        // seriesGd1Ref.current?.hide();
      }
      else if (currentPlot == "gamma_curves" && seriesTgxRef.current != undefined && seriesTgxRef.current.data != undefined) {
        seriesTgxRef.current.data.setAll(chartDataForAxis);
        seriesCeGxRef.current.data.setAll(chartDataForAxis);
        seriesPeGxRef.current.data.setAll(chartDataForAxis);
      }
      refreshAxisGuideLabels();
    }

  }, [props.plotParams, props.chartData, props.metadata]);


  function toggleFullScreen() {
    let chartdiv = document.querySelector(`#${props.id}`)
    if (!document.fullscreenElement) {
      chartdiv.requestFullscreen();
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  const disabledDate=(current)=> {
    return (current && current >= dayjs().endOf('day'))||dayjs(current).day()==6 || dayjs(current).day()==0
  }
  const onDateChange = (date, dateString) => {
    setStartDate(dayjs(date))
    console.log("Date changed",dayjs(date),dateString);
    props.handleStartDateChange(dateString);
  }

  //for dropdown control
  const handleDateChange=(event)=>{
    setStartDate(event.target.value);
    setDateParams(event.target.value);
    //console.log("Date changed",dayjs(date),event.target.value);
    props.handleStartDateChange(event.target.value);
  }

  const handlePlotTypeChange=(event)=>{
    setPlotType(event.target.value);
    props.changePlotType(event.target.value);
  }
  
  const handleExpiryChange=(event)=>{
    setExpiry(event.target.value);
    props.handleExpiryChange(event.target.value);
  }

  const handleTargetTimeChange=(event)=>{
    let index=timeInterval.findIndex((item)=>item.value==event.target.value)
    if(index!=-1){
      console.log("changed time=",timeInterval[index]);
      // if(timeInterval[index].value>=timeMinValue){
        setTargetTime(timeInterval[index])
        props.handleTimeChange(timeInterval[index]);
      // }
  }
  }

  return (
    <>
      <div id="container" style={{ backgroundColor: "#111111", zIndex: "99", marginTop: "0px", padding: "20px", width: "100%",minWidth:`${MIN_BAR_CHART_WIDTH}`, minHeight: "calc(110vh - 256px)" }}>
      {/* <div id="container" style={{ backgroundColor: "#111111", zIndex: "99", marginTop: "0px", padding: "20px", width: "100%", minHeight: "calc(110vh - 256px)" }}> */}
        {/* <LineChartHeader/> */}
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: "row", justifyContent: "space-evenly" }}>
          {/* <Tooltip title="Go Back">
            <IconButton
              onClick={() => navigate(-1)}
              size='large'>
              <ArrowBackIcon sx={{ width: "36px", height: "36px" }} />
            </IconButton>
          </Tooltip> */}
          {/* <Container sx={{ width: "80%" }}>
            <Typography variant='h4' color='primary' sx={{ alignSelf: "center" }}>{props.symbol} Options Chain Bar Chart at {props.metadata.time}</Typography>
          </Container>
          <Tooltip title="Full Screen">
            <IconButton
              onClick={() => toggleFullScreen()}
              size='large'>
              <FullscreenIcon sx={{ width: "36px", height: "36px" }} />
            </IconButton>
          </Tooltip> */}
        </Box>
        <div id="chartContainer">
        {isProUser?
          <>
          <Box display="flex" justifyContent="flex-start" sx={{mb:1,ml:1}}>
        
          {/* < Typography sx={{mt:1,ml:3,fontSize:16,width:"50px"}}>Expiry</Typography> */}
        
            <FormControl size="small" sx={{ width:"80px",minWidth:'120px',ml:2,mt:0 }}>
              <InputLabel id="demo-simple-select-label">Expiry</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={expiry}
                MenuProps={MenuProps}
                placeholder="Select Expiry"
                sx={{height:"40px"}}
                label="Expiry"
                onChange={(event)=>handleExpiryChange(event)}
              >
                {props.expiryList?.map((item,index) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </Select>
          </FormControl>
          <FormControl size="small" sx={{ width:"80px",minWidth:'120px',ml:2,mt:0 }}>
            <InputLabel id="demo-simple-select-label">Date</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={dateParams}
              MenuProps={MenuProps}
              sx={{height:"40px"}}
              label="Date"
              onChange={(event)=>handleDateChange(event)}
            >
              {props.dateList?.map((item,index) => (
              <MenuItem key ={item} value={item}>{item}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* <DatePicker onChange={onDateChange} 
                allowClear={false}
                disabledDate={disabledDate}
                value={startDate}
                placeholder="Select Date"
                format={DateFormat}
                style={{border: "1px solid #484848",background:"#111111",height:"40px",
                borderRadius: "4px",width:160,marginLeft:24,marginTop:"0px"}}
                />  */}
        
          {/* <Typography sx={{mt:1,ml:2,fontSize:16,width:"90px"}}>Plot Type</Typography> */}
          
          {menuType == "oi"
              ?
          <FormControl size="small" sx={{ width:"220px",minWidth:'250px',ml:3,mt:0 }}>
          <InputLabel id="demo-simple-select-label">Plot Type</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={plotType}
            MenuProps={MenuProps}
            sx={{height:"40px"}}
            label="Plot Type"
            onChange={(event)=>handlePlotTypeChange(event)}
          >
            
          {BAR_CHART_OI_PLOT_TYPE.map((item,index) => (
            <MenuItem key={item.key} value={item.value}>{item.key}</MenuItem>
          ))}
            
            
          </Select>
          </FormControl>  
          : 
          <>
          {isAdminUser ?
            <>
            <FormControl size="small" sx={{ width:"240px",minWidth:'250px',ml:3,mt:0 }}>
            <InputLabel id="demo-simple-select-label">Plot Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={plotType}
              MenuProps={MenuProps}
              sx={{height:"40px"}}
              label="Plot Type"
              onChange={(event)=>handlePlotTypeChange(event)}
            >
            {BAR_CHART_ADMIN_PLOT_TYPE.map((item,index) => (
              <MenuItem key={item.key} value={item.value}>{item.key}</MenuItem>
            ))}
            </Select>
            </FormControl>  
            </>
            :
            <>
            <FormControl size="small" sx={{ width:"220px",minWidth:'250px',ml:3,mt:0 }}>
            <InputLabel id="demo-simple-select-label">Plot Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={plotType}
              MenuProps={MenuProps}
              sx={{height:"40px"}}
              label="Plot Type"
              onChange={(event)=>handlePlotTypeChange(event)}
            >
            {BAR_CHART_MI_PLOT_TYPE.map((item,index) => (
              <MenuItem key={item.key} value={item.value}>{item.key}</MenuItem>
            ))}
            </Select>
            </FormControl>  
            </>
          }
          </>
          
        }
            <Box sx={{ width: 500,ml:8}}>
            <Slider
              // aria-label="Always visible"
              defaultValue={timeMinValue}
              value={targetTime.value}
              onChange={handleTargetTimeChange}
              step={1}
              marks={timeInterval}
              min={0}
              max={timeInterval[timeInterval.length-1].value}
            />  
            </Box>
            <Box>
              <Typography sx={{ml:4,marginTop:"4px",fontSize:"12px"}}>Time</Typography>
              <Typography sx={{ml:4,marginTop:"6px"}}>{targetTime.time}</Typography>
          </Box>
          <Button  variant="contained" disabled={!props.pollingPaused} onClick={()=>props.resetDateTime()} sx={{width:"115px",fontSize:12,fontColor:"#ffffff",height:30,mr:2,pl:1,ml:4,pr:1,mt:1}}  >
              Reset
            </Button>
        </Box>
        </>
        :
        isFastDataUser?
          <>
          {menuType == "oi"?
            <FormControl size="small" sx={{ width:"220px",minWidth:'250px',ml:3,mt:0 }}>
            <InputLabel id="demo-simple-select-label">Plot Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={plotType}
              MenuProps={MenuProps}
              sx={{height:"40px"}}
              label="Plot Type"
              onChange={(event)=>handlePlotTypeChange(event)}
            >
              
            {BAR_CHART_OI_PLOT_TYPE.map((item,index) => (
              <MenuItem key={item.key} value={item.value}>{item.key}</MenuItem>
            ))}
            </Select>
            </FormControl>
          : 
          <></>  
          }
          </>
        :
        <></>}
        {!props.isError?
        <>
          <div id="chartcontrols" style={{ padding: "10px", marginTop: "16px", maxWidth: "100%" }}></div>
          <div id={props.id} style={{ width: "100%", maxWidth: "100%", height: "800px" }} />
          </>:
          <>
          {/* Error state */}
        </>}
        </div>
      </div>
    </>
  )
}

export default OptChainChartContainer